import React, { createContext, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import { useAuth } from '../../authentication';
import { useGetSourcesQuery, useCreateSourceMutation, useDeleteSourceMutation } from '../hooks';
import { useOrganizations } from './OrganizationsProvider';
import { Source } from '../models';

const SourcesContext = createContext();

const SourcesProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();

  const sourcesQuery = useGetSourcesQuery(accessToken, currentOrganization?.id);
  const createSourceMutation = useCreateSourceMutation(accessToken);
  const deleteSourceMutation = useDeleteSourceMutation(accessToken);

  const createSource = useCallback(
    async (publicToken, integrationId, provider) => {
      await createSourceMutation.mutateAsync({
        organizationId: currentOrganization.id,
        publicToken,
        integrationId,
        provider
      });
      sourcesQuery.refetch();
    },
    [currentOrganization, createSourceMutation]
  );

  const deleteSource = useCallback(
    async (sourceId) => {
      await deleteSourceMutation.mutateAsync({
        sourceId,
        organizationId: currentOrganization.id
      });
      sourcesQuery.refetch();
    },
    [deleteSourceMutation, currentOrganization]
  );

  const value = useMemo(
    () => ({
      sources: sourcesQuery.data?.map((source) => new Source(source)),
      createSource,
      deleteSource
    }),
    [sourcesQuery.data]
  );

  return <SourcesContext.Provider value={value}>{children}</SourcesContext.Provider>;
};

SourcesProvider.propTypes = {
  children: PropTypes.node
};

const useSources = () => useContext(SourcesContext);

export { SourcesProvider, SourcesContext, useSources };
